
import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import AboutHero from '../components/about/AboutHero';
import ProcessSteps from '../components/about/ProcessSteps';
import TeamSection from '../components/about/TeamSection';
import { ArrowRight, CheckCircle, Users, Target, Lightbulb } from 'lucide-react';
import techStackData from '@/data/tech-stack.json';

interface TechStackItem {
  id: string
  category: string
  technologies: string[]
  image_url: string | null
  display_order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

const About = () => {
  const [techStack, setTechStack] = useState<TechStackItem[]>([])
  const [loading, setLoading] = useState(true)
  
  useEffect(() => {
    // Load static data
    const data = (techStackData as TechStackItem[])
      .filter(t => t.is_active)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    setTechStack(data)
    setLoading(false)
  }, [])

  const techCategories = [...new Set(techStack.map(item => item.category))]

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50/30">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-32 mb-4"></div>
            <div className="h-4 bg-slate-200 rounded w-48"></div>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      {/* Hero Section */}
      <AboutHero />

      {/* Mission & Vision */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            <div className="text-center animate-on-scroll">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-slate-900">Our Mission</h3>
              <p className="text-slate-600 leading-relaxed">
                To bridge the gap between cutting-edge technology and practical business solutions, 
                empowering organizations to thrive in the digital age.
              </p>
            </div>
            
            <div className="text-center animate-on-scroll" style={{ animationDelay: '0.1s' }}>
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Lightbulb className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-slate-900">Our Vision</h3>
              <p className="text-slate-600 leading-relaxed">
                To become the leading technology partner that transforms businesses through 
                innovative solutions, creative thinking, and unwavering commitment to excellence.
              </p>
            </div>
            
            <div className="text-center animate-on-scroll" style={{ animationDelay: '0.2s' }}>
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-slate-900">Our Values</h3>
              <p className="text-slate-600 leading-relaxed">
                Innovation, quality, and partnership form the foundation of everything we do, 
                ensuring we deliver solutions that exceed expectations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps Section */}
      <ProcessSteps />

      {/* Our Story Timeline */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-blue-50/30">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-20 animate-on-scroll">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">Our Journey</h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              From a small team of passionate developers to a trusted IT partner for businesses worldwide.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 animate-on-scroll">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-2 text-slate-900">Foundation</h4>
                  <p className="text-slate-600 leading-relaxed">
                    Founded with a vision to bridge the gap between cutting-edge technology and practical business solutions.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-2 text-slate-900">Growth</h4>
                  <p className="text-slate-600 leading-relaxed">
                    Expanded our expertise across multiple technologies and industries, building a diverse portfolio of successful projects.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-orange-500 to-pink-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-2 text-slate-900">Innovation</h4>
                  <p className="text-slate-600 leading-relaxed">
                    Continuously embracing new technologies and methodologies to deliver solutions that drive real business results.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="animate-on-scroll" style={{ animationDelay: '0.2s' }}>
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c" 
                alt="Our team collaboration" 
                className="w-full rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <TeamSection />

      {/* Technology Stack */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-blue-50/30">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-20 animate-on-scroll">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">Our Technology Stack</h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              We work with cutting-edge technologies to deliver robust and scalable solutions.
            </p>
          </div>
          
          {techStack.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-40 h-40 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <div className="text-slate-400 text-2xl">⚡</div>
              </div>
              <p className="text-slate-500 text-lg">No technologies found. Please run the data fetch script to populate content.</p>
            </div>
          ) : (
            <div className="space-y-12 max-w-6xl mx-auto">
              {techCategories.map((category, categoryIndex) => {
                const categoryTech = techStack.filter(item => item.category === category)[0]
                return (
                 <div
                    key={category}
                    className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100 flex flex-col lg:flex-row items-stretch animate-on-scroll"
                    style={{ animationDelay: `${categoryIndex * 0.1}s` }}
                  >
                    {/* Left side - background image */}
                    <div
                      className="lg:w-[30%] w-full bg-center bg-cover bg-no-repeat min-h-[200px] lg:min-h-auto"
                      style={{ backgroundImage: `url(${categoryTech?.image_url || '../../placeholder.svg'})` }}
                    ></div>

                    {/* Right side - Title and Tech Bricks */}
                    <div className="lg:w-[70%] w-full p-8 flex flex-col justify-center">
                      <h3 className="text-2xl font-bold text-slate-900 mb-4">{category}</h3>
                      <div className="flex flex-wrap gap-3">
                        {categoryTech?.technologies.map((technology) => (
                          <span
                            key={technology}
                            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-slate-50 to-slate-100 hover:from-primary/10 hover:to-blue-500/10 text-slate-700 hover:text-primary rounded text-sm font-medium shadow-sm hover:shadow-md transition-all duration-300 border border-slate-200 hover:border-primary/20"
                          >
                            {technology}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-r from-slate-900/90 via-slate-900/80 to-blue-950/90 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-purple-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container-custom relative z-10 text-center">
          <div className="max-w-3xl mx-auto animate-on-scroll">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">Ready to Work Together?</h2>
            <p className="text-xl text-slate-300 mb-10 leading-relaxed">
              Let's discuss how we can help transform your business with innovative technology solutions.
            </p>
            <a 
              href="/contact" 
              className="inline-flex items-center space-x-2 bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl"
            >
              <span>Get In Touch</span>
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
